import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';
import firebase from 'firebase/compat/app';
import { environment } from 'src/environments/environment';
import { Admin, Depot, MrBin, User } from 'src/models/models';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private yellowBinMailUrl = environment.yellowbinMail;

  constructor(
    private fireAuth: AngularFireAuth,
    private firestore: AngularFirestore,
    private router: Router,
    private httpClient: HttpClient
  ) {}

  registerAdmin(details: Admin, password) {
    // console.log(details);
  }

  forgotPassword(email) {
    return this.fireAuth.sendPasswordResetEmail(email);
  }

  registerMrBin(details: MrBin, password) {
    const { email, phone } = details;
    return this.fireAuth
      .createUserWithEmailAndPassword(email, password)
      .then((response) => {
        const uid = response.user.uid;
        const time = firebase.firestore.FieldValue.serverTimestamp();
        return this.firestore
          .collection('MrBins')
          .doc(uid)
          .set({
            ...details,
            userID: uid,
            createdAt: time,
          })
          .then(() => {
            this.firestore.collection('Users').doc(uid).set({
              userID: uid,
              role: 'MrBin',
              email,
              phone,
            });
          });
      });
  }

  registerUser(details: User, password) {
    const { email, phone } = details;
    return this.fireAuth
      .createUserWithEmailAndPassword(email, password)
      .then((response) => {
        const uid = response.user.uid;
        const time = firebase.firestore.FieldValue.serverTimestamp();
        return this.firestore
          .collection('Households')
          .doc(uid)
          .set({
            ...details,
            userID: uid,
            createdAt: time,
          })
          .then(() => {
            this.firestore.collection('Users').doc(uid).set({
              userID: uid,
              role: 'Household',
              email,
              phone,
            });
          });
      });
  }

  registerDepot(details: Depot, password) {
    const { email, phone, name } = details;
    return this.fireAuth
      .createUserWithEmailAndPassword(email, password)
      .then((response) => {
        const uid = response.user.uid;
        const time = firebase.firestore.FieldValue.serverTimestamp();
        response.user.sendEmailVerification();
        return this.firestore
          .collection('Depots')
          .doc(uid)
          .set({
            ...details,
            userID: uid,
            createdAt: time,
          })
          .then(() => {
            this.firestore.collection('Users').doc(uid).set({
              userID: uid,
              role: 'Depot',
              email,
              phone,
            });
            // this.sendMailToDepot(name, email);
          });
      });
  }

  sendMailToDepot(name: String, email: String) {
    const mailObj = { name, email };

    return this.httpClient
      .post(this.yellowBinMailUrl, JSON.stringify(mailObj), {
        responseType: 'text',
      })
      .pipe(take(1))
      .subscribe((res) => {
        // console.log(res);
      });
  }

  mrBinLogin(email, password, role) {
    return this.fireAuth.signInWithEmailAndPassword(email, password);
  }

  householdLogin(email, password, role) {
    return this.fireAuth.signInWithEmailAndPassword(email, password);
  }

  login(email, password, role) {
    return this.fireAuth
      .signInWithEmailAndPassword(email, password)
      .then((res) => {
        const id = res.user.uid;
        const user = { user: id, active: true, role };
        localStorage.setItem('yb_', JSON.stringify(user));
      });
  }

  adminLogin(email, password) {
    return this.fireAuth
      .signInWithEmailAndPassword(email, password)
      .then((res) => {
        const id = res.user.uid;
        const user = { user: id, active: true, role: 'Admin' };
        localStorage.setItem('yb_', JSON.stringify(user));
      });
  }

  checkUser(collection, email) {
    return this.firestore
      .collection(collection, (ref) => ref.where('email', '==', email))
      .get();
  }

  checkPhone(phone) {
    return this.firestore
      .collection('Users', (ref) => ref.where('phone', '==', phone))
      .get();
  }

  verifyID(userID) {
    return this.firestore.collection('Users').doc(userID).get();
  }

  logout() {
    return this.fireAuth.signOut().then(() => {
      this.router.navigate(['/home']).then(() => {
        localStorage.removeItem('yb_');
        location.reload();
      });
    });
  }

  setDriverPlayerID(userID, playerID) {
    return this.firestore.collection('Users').doc(userID).update({
      playerID,
    });
  }

  setHouseholdPlayerID(userID, playerID) {
    return this.firestore.collection('Users').doc(userID).update({
      playerID,
    });
  }

  getMrBinPlayerIDs() {
    return this.firestore
      .collection('Users', (ref) => ref.where('role', '==', 'MrBin'))
      .get();
  }

  isHouseholdAuthenticated() {
    const data = JSON.parse(localStorage.getItem('yb_'));
    if (data !== null) {
      const status = data.active;
      const role = data.role;
      if (status === true && role === 'Household') {
        return true;
      }
    }

    return false;
  }

  isMrBinAuthenticated() {
    const data = JSON.parse(localStorage.getItem('yb_'));
    if (data !== null) {
      const status = data.active;
      const role = data.role;
      if (status === true && role === 'MrBin') {
        return true;
      }
    }

    return false;
  }

  isDepotAuthenticated() {
    const data = JSON.parse(localStorage.getItem('yb_'));
    if (data !== null) {
      const status = data.active;
      const role = data.role;
      if (status === true && role === 'Depot') {
        return true;
      }
    }

    return false;
  }

  isAdminAuthenticated() {
    const data = JSON.parse(localStorage.getItem('yb_'));
    if (data !== null) {
      const status = data.active;
      const role = data.role;
      if (status === true && role === 'Admin') {
        return true;
      }
    }

    return false;
  }

  getRole() {
    const data = JSON.parse(localStorage.getItem('yb_'));
    if (data) {
      const role = data.role;
      return role;
    }

    return false;
  }
}
